<script setup>
/* global axios */
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { acquireMedia } from 'dashboard/helpers/mediaResource';
import { vPrivateSrc } from 'dashboard/composables/privateMediaDirective';
const props = defineProps({ conversationId: { type: Number, required: true } });
const route = useRoute(), store = useStore();
const open = ref(false), busy = ref(false), selected = ref(null), preview = ref(''), error = ref('');
let alive = true, selection = 0;
let libraryVersion = 0;
const tab = ref('recent'), library = ref({ recent: [], favorites: [] }), loading = ref(false);
const libraryUrl = computed(() => `/api/v1/accounts/${Number(route.params.accountId)}/sticker_library`);
const conversationStickers = computed(() => {
  const messages = store.getters.getSelectedChat?.messages || [];
  return messages.flatMap(message => message.attachments || [])
    .filter(attachment => String(attachment.extension || '').toLowerCase() === 'webp')
    .slice(-24).reverse();
});
const items = computed(() => tab.value === 'conversation' ? conversationStickers.value : library.value[tab.value]);
function isFavorite(item) { return item.favorite || library.value.favorites.some(x => x.id === item.id); }
async function loadLibrary() {
  const version = ++libraryVersion;
  loading.value = true;
  try {
    const { data } = await axios.get(libraryUrl.value);
    if (alive && libraryVersion === version) library.value = data;
  } catch {
    if (alive && libraryVersion === version) error.value = 'Não foi possível carregar sua coleção. Tente abrir novamente.';
  } finally { if (alive && libraryVersion === version) loading.value = false; }
}
async function toggle() {
  open.value = !open.value;
  if (open.value) { error.value = ''; await loadLibrary(); }
}
async function favorite(item) {
  if (busy.value) return;
  busy.value = true; error.value = '';
  try {
    await axios.post(libraryUrl.value, { attachment_id: item.id, operation: isFavorite(item) ? 'unfavorite' : 'favorite' });
    await loadLibrary();
  } catch { if (alive) error.value = 'Não foi possível atualizar suas favoritas. Tente novamente.'; }
  finally { if (alive) busy.value = false; }
}
watch(() => route.params.accountId, () => {
  libraryVersion++; library.value = { recent: [], favorites: [] }; selected.value = null;
  if (preview.value) URL.revokeObjectURL(preview.value);
  preview.value = ''; open.value = false;
});
async function select(file) {
  const version = ++selection;
  error.value = '';
  selected.value = null;
  if (preview.value) URL.revokeObjectURL(preview.value);
  preview.value = '';
  try {
    if (!file || file.type !== 'image/webp' || file.size > 1048576) throw Error('Selecione uma figurinha WebP de até 1 MB.');
    const bitmap = await createImageBitmap(file);
    const valid = bitmap.width <= 512 && bitmap.height <= 512;
    bitmap.close();
    if (!valid) throw Error('A figurinha deve ter até 512 × 512 pixels.');
    if (!alive || selection !== version) return;
    selected.value = file;
    preview.value = URL.createObjectURL(file);
  } catch (failure) {
    if (alive && selection === version) error.value = failure.message || 'Não foi possível abrir a figurinha.';
  }
}
async function chooseRecent(attachment) {
  if (busy.value) return;
  busy.value = true;
  let lease;
  try {
    lease = acquireMedia(attachment.data_url || attachment.dataUrl);
    const url = await lease.promise;
    const response = await fetch(url);
    const bytes = await response.blob();
    await select(new File([bytes], 'figurinha.webp', { type: 'image/webp' }));
  } catch { error.value = 'Não foi possível carregar esta figurinha.'; }
  finally { lease?.release(); busy.value = false; }
}
async function send() {
  if (busy.value || !selected.value) return;
  busy.value = true; error.value = '';
  const form = new FormData();
  form.append('message_type', 'outgoing'); form.append('private', 'false');
  form.append('content_attributes', JSON.stringify({ devlive_sticker: true }));
  form.append('attachments[]', selected.value, 'figurinha.webp');
  try {
    await axios.post(`/api/v1/accounts/${Number(route.params.accountId)}/conversations/${props.conversationId}/messages`, form, { timeout: 45000 });
    if (alive) { open.value = false; selected.value = null; }
  } catch {
    if (alive) { selected.value = null; error.value = 'Envio sem confirmação. Confira a conversa antes de tentar novamente.'; }
  } finally { if (alive) busy.value = false; }
}
onBeforeUnmount(() => { alive = false; selection++; libraryVersion++; if (preview.value) URL.revokeObjectURL(preview.value); });
</script>
<template>
  <div class="relative px-2 pb-1">
    <button type="button" class="px-2 py-1 text-sm text-n-slate-11 rounded hover:bg-n-alpha-2" :aria-expanded="open" @click="toggle">Figurinhas</button>
    <section v-if="open" aria-label="Enviar figurinha" class="absolute bottom-full left-0 z-30 w-72 max-w-[85vw] p-3 border border-n-weak rounded-lg bg-n-solid-2 shadow-lg" @keydown.esc="open = false">
      <div class="flex justify-between items-center mb-2"><strong class="text-sm">Figurinhas</strong><button type="button" aria-label="Fechar figurinhas" class="p-2" @click="open = false">×</button></div>
      <p class="text-xs text-n-slate-11 mb-2">Sua coleção pode ser usada em outras conversas.</p>
      <div class="flex gap-1 mb-2" role="group" aria-label="Coleções de figurinhas">
        <button v-for="option in [{ key: 'recent', name: 'Recentes' }, { key: 'favorites', name: 'Favoritas' }, { key: 'conversation', name: 'Nesta conversa' }]" :key="option.key" type="button" :aria-pressed="tab === option.key" class="px-2 py-2 text-xs rounded hover:bg-n-alpha-2" :class="{ 'bg-n-alpha-2': tab === option.key }" @click="tab = option.key">{{ option.name }}</button>
      </div>
      <p v-if="loading" role="status" class="text-xs py-3">Carregando coleção…</p>
      <p v-else-if="!items.length" class="text-xs py-3 text-n-slate-11">{{ tab === 'favorites' ? 'Marque a estrela de uma figurinha para guardá-la aqui.' : tab === 'recent' ? 'Suas figurinhas enviadas aparecem aqui, em qualquer conversa.' : 'Ainda não há figurinhas nesta conversa.' }}</p>
      <div v-if="items.length" class="grid grid-cols-4 gap-2 max-h-48 overflow-auto mb-2">
        <div v-for="attachment in items" :key="attachment.id" class="flex flex-col items-center">
        <button type="button" aria-label="Selecionar figurinha" :disabled="busy" @click="chooseRecent(attachment)">
          <img v-private-src="attachment.data_url || attachment.dataUrl" class="w-12 h-12 object-contain" alt="Figurinha" />
        </button>
        <button type="button" :aria-label="isFavorite(attachment) ? 'Remover das favoritas' : 'Adicionar às favoritas'" :aria-pressed="!!isFavorite(attachment)" :disabled="busy" class="px-3 py-1 text-base text-n-slate-11" @click="favorite(attachment)">{{ isFavorite(attachment) ? '★' : '☆' }}</button>
        </div>
      </div>
      <label class="block text-sm">Adicionar WebP (até 512 × 512, 1 MB)<input type="file" accept="image/webp,.webp" :disabled="busy" class="block w-full mt-2 text-xs" @change="select($event.target.files?.[0])" /></label>
      <img v-if="preview" :src="preview" alt="Prévia da figurinha" class="mx-auto h-24 w-24 object-contain my-2" />
      <p v-if="error" role="status" class="text-sm mt-2">{{ error }}</p>
      <button type="button" :disabled="busy || !selected" class="mt-2 px-3 py-2 rounded bg-n-brand text-white disabled:opacity-50" @click="send">{{ busy ? 'Enviando…' : 'Enviar figurinha' }}</button>
    </section>
  </div>
</template>
